import { useEffect, useState } from 'react';
import {
  bulkUpdateOrder,
  OrderConflictError,
  type ItemChange,
  type ItemShape,
} from '../../lib/orders';
import {
  MAX_BULK_ADD,
  PRIMARY_OPTIONS,
  PRIMARY_SKU_REINIGEN,
  getDefaultPackageForPrimary,
  getPackagesForPrimary,
  isPerStukPrimary,
} from '../../lib/order-edit-rules';

/**
 * Snel-toevoegen van items op de order-detailpagina — zelfde modal-UX
 * als de offerte-flow (AddKleedDialog in offerte-detail), maar mét expliciete
 * pakketkeuze. Het Aantal-veld geldt voor álle producten: bij m²-producten
 * gaan er N identieke `add`-ItemChanges in één `bulkUpdateOrder`-call mee
 * (maten per kleed daarna aanpassen via het potlood-icoon); volledige
 * bewerking blijft via "Bewerken".
 *
 * Per-stuk producten (Kussen) hebben geen vorm/maten/pakket — daar gaat
 * `aantal` als Aantal__c mee op één ItemChange.
 */
interface AddKleedDialogProps {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onAdded: (nieuweLastModifiedDate: string | null) => void;
}

const VORM_OPTIONS: { value: ItemShape; label: string }[] = [
  { value: 'rechthoekig', label: 'Rechthoekig' },
  { value: 'rond', label: 'Rond' },
  { value: 'overig', label: 'Overig' },
];

export function AddKleedDialog({ isOpen, orderId, onClose, onAdded }: AddKleedDialogProps) {
  const [primarySku, setPrimarySku] = useState(PRIMARY_SKU_REINIGEN);
  const [packageSku, setPackageSku] = useState<string | null>(
    getDefaultPackageForPrimary(PRIMARY_SKU_REINIGEN)
  );
  const [vorm, setVorm] = useState<ItemShape>('rechthoekig');
  const [lengte, setLengte] = useState('');
  const [breedte, setBreedte] = useState('');
  const [diameter, setDiameter] = useState('');
  const [oppervlakte, setOppervlakte] = useState('');
  const [aantal, setAantal] = useState('1');
  const [ondertapijt, setOndertapijt] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPrimarySku(PRIMARY_SKU_REINIGEN);
    setPackageSku(getDefaultPackageForPrimary(PRIMARY_SKU_REINIGEN));
    setVorm('rechthoekig');
    setLengte('');
    setBreedte('');
    setDiameter('');
    setOppervlakte('');
    setAantal('1');
    setOndertapijt(false);
    setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const perStuk = isPerStukPrimary(primarySku);
  const packagesForPrimary = getPackagesForPrimary(primarySku);

  const handlePrimaryChange = (sku: string) => {
    setPrimarySku(sku);
    setPackageSku(getDefaultPackageForPrimary(sku));
  };

  const aantalNum = Number(aantal);
  const aantalValid =
    Number.isInteger(aantalNum) && aantalNum >= 1 && aantalNum <= MAX_BULK_ADD;

  const valid = (() => {
    if (!aantalValid) return false;
    if (perStuk) return true;
    if (!packageSku) return false;
    if (vorm === 'rechthoekig') return Number(lengte) > 0 && Number(breedte) > 0;
    if (vorm === 'rond') return Number(diameter) > 0;
    return Number(oppervlakte) > 0;
  })();

  async function handleSubmit() {
    if (!perStuk && !packageSku) {
      setError('Kies een pakket');
      return;
    }
    setSaving(true);
    setError(null);

    const changes: ItemChange[] = perStuk
      ? [{ kind: 'add', primarySku, aantal: aantalNum }]
      : Array.from({ length: aantalNum }, () => {
          const change: ItemChange = {
            kind: 'add',
            vorm,
            primarySku,
            packageSku: packageSku!,
            ondertapijt,
          };
          if (vorm === 'rechthoekig') {
            change.lengteCm = Number(lengte);
            change.breedteCm = Number(breedte);
          } else if (vorm === 'rond') {
            change.diameterCm = Number(diameter);
          } else {
            change.oppervlakteM2 = Number(oppervlakte);
          }
          return change;
        });

    try {
      const result = await bulkUpdateOrder(orderId, changes);
      onAdded(result.nieuweLastModifiedDate);
      onClose();
    } catch (err) {
      if (err instanceof OrderConflictError) {
        setError(
          'Deze order is ondertussen gewijzigd door iemand anders. Ververs de pagina om de laatste versie te zien.'
        );
      } else {
        setError(err instanceof Error ? err.message : 'Toevoegen mislukt');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <h3 className="text-base font-semibold text-slate-900">Item toevoegen</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
            aria-label="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <label className="block">
            <span className="text-xs text-slate-600">Product</span>
            <select
              value={primarySku}
              onChange={(e) => handlePrimaryChange(e.target.value)}
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
            >
              {PRIMARY_OPTIONS.map((p) => (
                <option key={p.sku} value={p.sku}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-xs text-slate-600">Aantal</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_BULK_ADD}
              step={1}
              value={aantal}
              onChange={(e) => setAantal(e.target.value)}
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
            />
            {!perStuk && aantalValid && aantalNum > 1 && (
              <span className="mt-1 block text-xs text-slate-500">
                Alle {aantalNum} items krijgen dezelfde maat en hetzelfde pakket; per kleed
                aan te passen na toevoegen.
              </span>
            )}
          </label>

          {!perStuk && (
          <label className="block">
            <span className="text-xs text-slate-600">Pakket</span>
            <select
              value={packageSku ?? ''}
              onChange={(e) => setPackageSku(e.target.value || null)}
              className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
            >
              {packagesForPrimary.map((p) => (
                <option key={p.sku} value={p.sku}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          )}

          {!perStuk && (
          <div>
            <span className="text-xs text-slate-600">Vorm</span>
            <div className="mt-1 inline-flex rounded-md border border-slate-300 overflow-hidden">
              {VORM_OPTIONS.map((opt, i) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVorm(opt.value)}
                  className={
                    'px-3 py-1.5 text-sm ' +
                    (vorm === opt.value
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50') +
                    (i > 0 ? ' border-l border-slate-300' : '')
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {!perStuk && vorm === 'rechthoekig' && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-slate-600">Lengte (cm)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={lengte}
                  onChange={(e) => setLengte(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs text-slate-600">Breedte (cm)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={breedte}
                  onChange={(e) => setBreedte(e.target.value)}
                  className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
                />
              </label>
            </div>
          )}

          {!perStuk && vorm === 'rond' && (
            <label className="block">
              <span className="text-xs text-slate-600">Diameter (cm)</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          )}

          {!perStuk && vorm === 'overig' && (
            <label className="block">
              <span className="text-xs text-slate-600">Oppervlakte (m²)</span>
              <input
                type="number"
                inputMode="decimal"
                min={0.01}
                step={0.01}
                value={oppervlakte}
                onChange={(e) => setOppervlakte(e.target.value)}
                className="mt-1 w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm"
              />
            </label>
          )}

          {!perStuk && (
          <label className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              checked={ondertapijt}
              onChange={(e) => setOndertapijt(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-slate-700">Ondertapijt op maat toevoegen</span>
          </label>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !valid}
            className="text-sm px-3 py-1.5 rounded-md bg-indigo-600 text-white font-medium hover:bg-indigo-700 disabled:bg-slate-300"
          >
            {saving
              ? 'Toevoegen…'
              : aantalValid && aantalNum > 1
                ? `Toevoegen (${aantalNum})`
                : 'Toevoegen'}
          </button>
        </div>
      </div>
    </div>
  );
}
